import React, { useState } from "react";
import { Box, Container, Paper, Grid, Typography, TextField, Button, CircularProgress, Alert, Fade, LinearProgress, IconButton, InputAdornment, Stack, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useRef } from "react";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { fetchJson } from './api';


const ENABLE_IMAGINE_AUTO_DELETE = (process.env.REACT_APP_IMAGINE_AUTO_DELETE || "").toLowerCase() === "true";

function ImaginePage() {
  // Refs for press-and-hold steppers
  const widthInterval = useRef(null);
  const heightInterval = useRef(null);

  const startStepper = (setter, min, max, delta, setLiveError) => {
    setLiveError("");
    setResError("");
    return setInterval(() => {
      setter(prev => String(Math.max(min, Math.min(max, (Number(prev) || min) + delta))));
    }, 80);
  };
  const [prompt, setPrompt] = useState("");
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [imageDeleted, setImageDeleted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // disables Generate button
  const [promptError, setPromptError] = useState(""); // for word limit
  const [width, setWidth] = useState("512");
  const [height, setHeight] = useState("512");
  const [email, setEmail] = useState("");
  const [resError, setResError] = useState("");
  const [widthLiveError, setWidthLiveError] = useState("");
  const [heightLiveError, setHeightLiveError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [modelId, setModelId] = useState("stabilityai/sd-turbo");

  // Recent jobs ("queue") polling
  const [jobs, setJobs] = useState([]);
  const [jobsError, setJobsError] = useState(null);
  const jobsRequest = useRef(false);

  React.useEffect(() => () => {
    clearInterval(widthInterval.current);
    clearInterval(heightInterval.current);
  }, []);

  const fetchJobs = React.useCallback(async () => {
    if (jobsRequest.current) return;
    jobsRequest.current = true;
    try {
      const data = await fetchJson('/imagine/jobs?limit=25');
      if (!Array.isArray(data)) throw new Error('The image backend returned an invalid job list.');
      setJobs(data);
      setJobsError(null);
    } catch (e) {
      setJobsError(e.message || "Error loading queue");
    } finally {
      jobsRequest.current = false;
    }
  }, []);

  const clearQueue = async () => {
    const ok = window.confirm(
      "Clear local queue + jobs + generated images? This cannot be undone."
    );
    if (!ok) return;
    try {
      await fetchJson('/imagine/admin/clear', {
        method: "POST",
      });
      // Reset current tracking state
      setJobId(null);
      setStatus(null);
      setImageUrl(null);
      setError(null);
      setProgress(0);
      setTimerActive(false);
      setJobsError(null);
      await fetchJobs();
    } catch (e) {
      setJobsError(e.message || "Error clearing queue");
    }
  };

  React.useEffect(() => {
    if (jobsError) return;
    fetchJobs();
    const interval = setInterval(fetchJobs, 2000);
    return () => clearInterval(interval);
  }, [fetchJobs, jobsError]);

  // Poll job status
  React.useEffect(() => {
    if (!jobId) return;
    setStatus("pending");
    setImageUrl(null);
    setError(null);
    setImageDeleted(false);
    setTimerActive(true);
    setProgress(0);
    let active = true;
    let checking = false;
    const interval = setInterval(async () => {
      if (checking) return;
      checking = true;
      try {
        const data = await fetchJson(`/imagine/status/${encodeURIComponent(jobId)}`);
        if (!active) return;
        if (!['pending', 'processing', 'completed', 'failed'].includes(data.status)) {
          throw new Error('The image backend returned an invalid job status.');
        }
        setStatus(data.status);
        if (data.status === "completed") {
          if (!data.image_url) throw new Error('The completed job has no image.');
          setImageUrl(data.image_url);
          setProgress(100);
          setTimerActive(false);
          clearInterval(interval);
        } else if (data.status === "failed") {
          setError(data.error || "Generation failed");
          setProgress(100);
          setTimerActive(false);
          clearInterval(interval);
        }
      } catch (e) {
        if (!active) return;
        setError(e.message || "Error checking status");
        setStatus('failed');
        setProgress(100);
        setTimerActive(false);
        clearInterval(interval);
      } finally {
        checking = false;
      }
    }, 2000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [jobId]);

  // Progress bar timer (10 minutes max, completes early if job finishes)
  React.useEffect(() => {
    if (!timerActive) return;
    let start = Date.now();
    let duration = 10 * 60 * 1000; // 10 minutes in ms
    setProgress(0);
    const timer = setInterval(() => {
      setProgress(prev => {
        // If progress was set to 100% by job completion, stop timer
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        const elapsed = Date.now() - start;
        const percent = Math.min(95, (elapsed / duration) * 95);
        if (elapsed >= duration) clearInterval(timer);
        return percent;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timerActive]);

  // NOTE: For local dev we keep generated images so they can be clicked/viewed later.
  // In production you can opt-in to auto-delete by setting REACT_APP_IMAGINE_AUTO_DELETE=true.
  React.useEffect(() => {
    if (!ENABLE_IMAGINE_AUTO_DELETE) return;
    if (!imageUrl || imageDeleted) return;
    const img = new window.Image();
    img.onload = async () => {
      try {
        const blobName = new URL(imageUrl, window.location.origin).pathname.split('/').pop();
        await fetchJson(`/imagine/blob/email-and-delete/${encodeURIComponent(blobName)}`, { method: "POST" });
        setImageDeleted(true);
      } catch (e) {
        setError(e.message || 'The image could not be emailed and deleted.');
      }
    };
    img.src = imageUrl;
    return () => { img.onload = null; };
  }, [imageUrl, imageDeleted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent double submit
    // Check word limit
    const wordCount = prompt.trim().split(/\s+/).filter(Boolean).length;
    if (!wordCount || wordCount > 77) {
      setPromptError("Enter a prompt of 1-77 words.");
      return;
    }
    // Validate resolution
    const widthNum = Number(width);
    const heightNum = Number(height);
    if (
      !Number.isInteger(widthNum) || !Number.isInteger(heightNum) ||
      widthNum < 256 || widthNum > 1920 ||
      heightNum < 256 || heightNum > 1072 ||
      widthNum % 16 !== 0 || heightNum % 16 !== 0
    ) {
      setResError("Width must be 256-1920, height 256-1072, in multiples of 16.");
      return;
    } else {
      setResError("");
    }
    // Validate email (optional)
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setEmailError("Invalid email address.");
      return;
    } else {
      setEmailError("");
    }
    setIsSubmitting(true);
    setJobId(null);
    setStatus(null);
    setImageUrl(null);
    setError(null);
    setProgress(0);
    setTimerActive(false);
    setPromptError("");
    try {
      const data = await fetchJson('/imagine', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: prompt.trim(), width: widthNum, height: heightNum, email: email.trim() || null, model_id: modelId }),
      });
      if (data.job_id) {
        setJobId(data.job_id);
        fetchJobs();
      } else {
        setError(data.error || "Failed to submit prompt");
      }
    } catch (e) {
      setError(e.message || "Error submitting prompt");
    }
    setIsSubmitting(false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, var(--white) 0%, rgba(165, 209, 225, 0.1) 100%)', 
      pt: 4, 
      pb: 8 
    }}>
      <Container maxWidth="md">
        <Typography variant="h3" fontWeight={800} align="center" sx={{ 
          color: 'var(--rosewood)', 
          mb: 1,
          letterSpacing: '-1px'
        }}>
          Imagine
        </Typography>
        <Typography variant="h6" align="center" sx={{ color: 'var(--chinese-violet)', mb: 6, fontWeight: 400 }}>
          Generate AI images with your custom prompt
        </Typography>
        <Alert role="status" severity="info" sx={{ mb: 3 }}>
          Image generation uses the configured Python backend and worker, not a sample image.
          {' '}<a href="/.auth/login/github?post_login_redirect_uri=/imagine">Sign in as an invited GitHub admin</a> to generate and manage images.
        </Alert>

        <Stack spacing={4} sx={{ mb: 6, width: '100%' }}>
          {/* Top Panel: Controls */}
          <Box>
            <Paper elevation={0} sx={{ 
              p: 4, 
              width: '100%',
              borderRadius: 4, 
              background: 'var(--white)',
              border: '1px solid rgba(0,0,0,0.05)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}>
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    label="Enter your prompt"
                    multiline
                    rows={4}
                    fullWidth
                    value={prompt}
                    onChange={(e) => {
                      setPrompt(e.target.value);
                      const wc = e.target.value.trim().split(/\s+/).filter(Boolean).length;
                      if (wc > 77) {
                        setPromptError("Prompt must be 77 words or fewer.");
                      } else {
                        setPromptError("");
                      }
                    }}
                    error={!!promptError}
                    helperText={promptError || `${prompt.trim().split(/\s+/).filter(Boolean).length}/77 words`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        '&.Mui-focused fieldset': { borderColor: 'var(--pacific-cyan)' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: 'var(--pacific-cyan)' }
                    }}
                  />

                  <FormControl fullWidth size="small">
                    <InputLabel id="imagine-model-label">AI Model</InputLabel>
                    <Select
                      labelId="imagine-model-label"
                      value={modelId}
                      label="AI Model"
                      onChange={(e) => setModelId(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="stabilityai/sd-turbo">SD Turbo (Fastest, Lower Quality)</MenuItem>
                      <MenuItem value="stabilityai/sdxl-turbo">SDXL Turbo (Fast, Better Quality)</MenuItem>
                      <MenuItem value="stabilityai/stable-diffusion-3.5-medium">SD 3.5 Medium (Slower, Highest Quality)</MenuItem>
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'var(--chinese-violet)', mb: 1, fontWeight: 600 }}>
                      Dimensions
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Width"
                        value={width}
                        onChange={e => {
                          setWidth(e.target.value);
                          setResError("");
                          const v = Number(e.target.value);
                          if (e.target.value === "" || !Number.isInteger(v)) setWidthLiveError("Whole-number width required");
                          else if (v < 256 || v > 1920) setWidthLiveError("256-1920 only");
                          else if (v % 16 !== 0) setWidthLiveError("Must be divisible by 16");
                          else setWidthLiveError("");
                        }}
                        size="small"
                        fullWidth
                        required
                        error={!!widthLiveError}
                        helperText={widthLiveError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                onClick={() => {
                                  let v = parseInt(width, 10) || 256;
                                  v = Math.max(256, v - 16);
                                  setWidth(String(v));
                                  setResError("");
                                  if (v < 256 || v > 1920) setWidthLiveError("256-1920 only");
                                  else setWidthLiveError("");
                                }}
                                onMouseDown={e => {
                                  if (e.button !== 0) return;
                                  if (widthInterval.current) clearInterval(widthInterval.current);
                                  widthInterval.current = startStepper(setWidth, 256, 1920, -16, setWidthLiveError);
                                }}
                                onMouseUp={() => { if (widthInterval.current) clearInterval(widthInterval.current); }}
                                onMouseLeave={() => { if (widthInterval.current) clearInterval(widthInterval.current); }}
                                edge="start"
                                aria-label="Decrease width"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => {
                                  let v = parseInt(width, 10) || 256;
                                  v = Math.min(1920, v + 16);
                                  setWidth(String(v));
                                  setResError("");
                                  if (v < 256 || v > 1920) setWidthLiveError("256-1920 only");
                                  else setWidthLiveError("");
                                }}
                                onMouseDown={e => {
                                  if (e.button !== 0) return;
                                  if (widthInterval.current) clearInterval(widthInterval.current);
                                  widthInterval.current = startStepper(setWidth, 256, 1920, 16, setWidthLiveError);
                                }}
                                onMouseUp={() => { if (widthInterval.current) clearInterval(widthInterval.current); }}
                                onMouseLeave={() => { if (widthInterval.current) clearInterval(widthInterval.current); }}
                                edge="end"
                                aria-label="Increase width"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        inputProps={{ min: 256, max: 1920, step: 16, inputMode: 'numeric', pattern: '[0-9]*' }}
                      />
                      <TextField
                        label="Height"
                        value={height}
                        onChange={e => {
                          setHeight(e.target.value);
                          setResError("");
                          const v = Number(e.target.value);
                          if (e.target.value === "" || !Number.isInteger(v)) setHeightLiveError("Whole-number height required");
                          else if (v < 256 || v > 1072) setHeightLiveError("256-1072 only");
                          else if (v % 16 !== 0) setHeightLiveError("Must be divisible by 16");
                          else setHeightLiveError("");
                        }}
                        size="small"
                        fullWidth
                        required
                        error={!!heightLiveError}
                        helperText={heightLiveError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                onClick={() => {
                                  let v = parseInt(height, 10) || 256;
                                  v = Math.max(256, v - 16);
                                  setHeight(String(v));
                                  setResError("");
                                  if (v < 256 || v > 1072) setHeightLiveError("256-1072 only");
                                  else setHeightLiveError("");
                                }}
                                onMouseDown={e => {
                                  if (e.button !== 0) return;
                                  if (heightInterval.current) clearInterval(heightInterval.current);
                                  heightInterval.current = startStepper(setHeight, 256, 1072, -16, setHeightLiveError);
                                }}
                                onMouseUp={() => { if (heightInterval.current) clearInterval(heightInterval.current); }}
                                onMouseLeave={() => { if (heightInterval.current) clearInterval(heightInterval.current); }}
                                edge="start"
                                aria-label="Decrease height"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => {
                                  let v = parseInt(height, 10) || 256;
                                  v = Math.min(1072, v + 16);
                                  setHeight(String(v));
                                  setResError("");
                                  if (v < 256 || v > 1072) setHeightLiveError("256-1072 only");
                                  else setHeightLiveError("");
                                }}
                                onMouseDown={e => {
                                  if (e.button !== 0) return;
                                  if (heightInterval.current) clearInterval(heightInterval.current);
                                  heightInterval.current = startStepper(setHeight, 256, 1072, 16, setHeightLiveError);
                                }}
                                onMouseUp={() => { if (heightInterval.current) clearInterval(heightInterval.current); }}
                                onMouseLeave={() => { if (heightInterval.current) clearInterval(heightInterval.current); }}
                                edge="end"
                                aria-label="Increase height"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        inputProps={{ min: 256, max: 1072, step: 16, inputMode: 'numeric', pattern: '[0-9]*' }}
                      />
                    </Stack>
                    {resError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{resError}</Alert>}
                  </Box>

                  <TextField
                    label="Email (optional)"
                    fullWidth
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError(e.target.value && !/^\S+@\S+\.\S+$/.test(e.target.value.trim()) ? 'Invalid email address.' : '');
                    }}
                    placeholder="Receive result via email"
                    size="small"
                    error={!!emailError}
                    helperText={emailError || "The worker attempts email delivery when an address is provided. A completed image does not guarantee email delivery."}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&.Mui-focused fieldset': { borderColor: 'var(--pacific-cyan)' }
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isSubmitting || !prompt.trim() || !!promptError || !!resError || !!emailError || !!widthLiveError || !!heightLiveError}
                    sx={{
                      background: 'linear-gradient(135deg, var(--madder) 0%, var(--crimson) 100%)',
                      borderRadius: 3,
                      py: 1.5,
                      fontWeight: 700,
                      boxShadow: '0 4px 15px rgba(188, 35, 50, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, var(--crimson) 0%, var(--rose-red) 100%)',
                        boxShadow: '0 6px 20px rgba(188, 35, 50, 0.4)'
                      }
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Generate Image"}
                  </Button>

                  {error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}
                </Stack>
              </form>
            </Paper>
          </Box>

          {/* Middle Panel: Result */}
          <Box>
            <Paper elevation={0} sx={{ 
              p: 4, 
              width: '100%',
              borderRadius: 4, 
              background: 'var(--white)',
              border: '1px solid rgba(0,0,0,0.05)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              position: 'relative',
              overflow: 'hidden'
            }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!imageUrl && !status && (
          <Box sx={{ textAlign: 'center', color: 'var(--chinese-violet)', opacity: 0.6 }}>
          <ImageIcon sx={{ fontSize: 64, mb: 2, color: 'var(--light-blue)' }} />
          <Typography variant="h6">Your imagination awaits</Typography>
          </Box>
        )}

        {(status === "pending" || status === "processing") && (
          <Box sx={{ width: '100%', maxWidth: 300, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: 'var(--pacific-cyan)', mb: 3 }} />
          <Typography variant="h6" sx={{ color: 'var(--black)', mb: 1 }}>Creating your masterpiece...</Typography>
          <Typography variant="body2" sx={{ color: 'var(--chinese-violet)', mb: 2 }}>This may take a few minutes</Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ 
            height: 8, 
            borderRadius: 4,
            bgcolor: 'var(--light-blue)',
            '& .MuiLinearProgress-bar': { bgcolor: 'var(--pacific-cyan)' }
          }} />
          </Box>
        )}

        {status === 'failed' && (
          <Box sx={{ textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 64, mb: 2, color: 'var(--crimson)' }} />
          <Typography variant="h6" sx={{ color: 'var(--rosewood)' }}>Generation Failed</Typography>
          {error && <Typography sx={{ color: 'var(--black)' }}>{error}</Typography>}
          </Box>
        )}

        {imageUrl && (
          <Fade in={true}>
          <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img 
            src={imageUrl} 
            alt="Generated" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '600px', 
              borderRadius: '12px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.1)' 
            }} 
            />
          </Box>
          </Fade>
        )}
        </Box>
            </Paper>
          </Box>

        {/* Bottom Panel: Queue / Recent Jobs */}
        <Box>
          <Paper elevation={0} sx={{ 
            p: 4, 
            width: '100%',
          borderRadius: 4, 
          background: 'var(--white)',
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--rosewood)' }}>
              Queue / Recent Jobs
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={clearQueue}
              sx={{
                borderColor: 'var(--crimson)',
                color: 'var(--crimson)',
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: 2
              }}
            >
              Clear Queue
            </Button>
          </Box>
          
          {jobsError && (
            <Alert severity="warning" action={<Button color="inherit" onClick={() => setJobsError(null)}>Retry</Button>} sx={{ borderRadius: 2, mb: 3 }}>
              {jobsError}
            </Alert>
          )}
          
          <Box sx={{ maxHeight: 400, overflowY: 'auto', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', bgcolor: '#f8fafc' }}>
            {jobs.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'var(--chinese-violet)', opacity: 0.8 }}>
                <Typography variant="body1">{jobsError ? 'Queue data is unavailable.' : 'No jobs yet. Generate an image to see it here.'}</Typography>
              </Box>
            ) : (
              <Stack spacing={2} sx={{ p: 2 }}>
                {jobs.map((j) => (
                  <Paper
                    key={j.job_id}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: '1px solid rgba(0,0,0,0.06)',
                      background: 'var(--white)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                        <Typography variant="caption" sx={{ color: 'var(--chinese-violet)', fontFamily: 'monospace' }}>
                          {j.job_id}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'var(--black)', fontWeight: 600, mt: 0.5 }} noWrap title={j.prompt}>
                          {j.prompt}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--chinese-violet)', display: 'block', mt: 0.5 }}>
                          Model: <Box component="span" sx={{ fontWeight: 600 }}>{j.model_id || "stabilityai/sd-turbo"}</Box>
                        </Typography>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                          <Typography
                            variant="button"
                            sx={{
                              color: j.status === 'completed' ? 'var(--pacific-cyan)' : j.status === 'failed' ? 'var(--crimson)' : 'var(--chinese-violet)',
                              fontWeight: 800,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: j.status === 'completed' ? 'rgba(0, 168, 232, 0.1)' : j.status === 'failed' ? 'rgba(188, 35, 50, 0.1)' : 'rgba(165, 209, 225, 0.2)'
                            }}
                          >
                            {j.status}
                          </Typography>
                          
                          {j.status === 'completed' && j.image_url && (
                            <Button
                              size="small"
                              variant="contained"
                              href={j.image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                bgcolor: 'var(--pacific-cyan)',
                                color: 'white',
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#008bbf', boxShadow: 'none' }
                              }}
                            >
                              Open
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setJobId(j.job_id)}
                            sx={{
                              borderColor: 'var(--chinese-violet)',
                              color: 'var(--chinese-violet)',
                              fontWeight: 700,
                              textTransform: 'none'
                            }}
                          >
                            Track
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                    
                    {j.status === 'completed' && j.job_id === jobId && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'rgba(0, 168, 232, 0.05)', borderRadius: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 18, color: 'var(--pacific-cyan)' }} />
                        <Typography variant="body2" sx={{ color: 'var(--pacific-cyan)', fontWeight: 600 }}>
                          Currently Selected
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
          </Paper>
        </Box>
      </Stack>
      </Container>
    </Box>
  );
}

export default ImaginePage;